import { useSyncExternalStore } from "react";
import { AnyType } from "../interfaces";
type Dispatch<T> = (value: T | ((prev: T) => T)) => void;
type UseStore<T> = () => T;

export default function createStore<T>(initialStore: T): [UseStore<T>, Dispatch<T>] {
  let store = initialStore;
  const listeners = new Set<(getSnapshot: () => T) => void>();

  function getStore(): T {
    return store;
  }

  function dispatch(newStore: T | ((prev: T) => T)) {
    const oldStore = store;

    // Make it like reacts setState
    // so if you pass in a function you can get the store value first
    store = typeof newStore === "function" ? (newStore as ((prev: T) => T))(store) : newStore;

    if(!shouldUpdate(oldStore, store)) {
      return;
    }

    listeners.forEach(listener => listener(() => store));
  }

  function subscribe(listener: (getSnapshot: () => T) => void) {
    listeners.add(listener);

    return function() {
      listeners.delete(listener);
    }
  }

  function useStore(): T {
    return useSyncExternalStore(subscribe, getStore);
  }

  function shouldUpdate(a: unknown, b: unknown): boolean {
    if((!a || !b) && (a !== b)) {
      return true;
    }

    if(typeof a !== "object" || typeof b !== "object") {
      return true;
    }

    if(Object.keys((a as object) || {}).length !== Object.keys((b as object) || {}).length) {
      return true;
    }

    // (b) needs to be an object or you will have to do checks before looping
    // make sure your store is an object or check it in this function.
    for(const key in b as object) {
      if((b as AnyType)[key] !== (a as AnyType)[key]) {
        return true;
      }
    }

    return false;
  }

  return [useStore, dispatch]
}
